import { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { api, setAccessToken, getAccessToken } from '../api/client.js';

const AuthContext = createContext(null);

export function AuthProvider({ children }) {
  const [customer, setCustomer] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchMe = useCallback(async () => {
    try {
      const data = await api('/account/me');
      setCustomer(data.customer);
    } catch {
      setCustomer(null);
      setAccessToken(null);
    }
  }, []);

  useEffect(() => {
    const tryRefresh = async () => {
      try {
        const res = await fetch('/api/auth/customer/refresh', {
          method: 'POST',
          credentials: 'include',
        });
        if (res.ok) {
          const data = await res.json();
          setAccessToken(data.accessToken);
          await fetchMe();
        }
      } catch {
        // no session
      } finally {
        setLoading(false);
      }
    };
    tryRefresh();
  }, [fetchMe]);

  const login = useCallback(async (email, password) => {
    const data = await api('/auth/customer/login', {
      method: 'POST',
      body: { email, password },
    });
    setAccessToken(data.accessToken);
    setCustomer(data.customer);
    return data;
  }, []);

  const signup = useCallback(async (payload) => {
    const data = await api('/auth/customer/signup', {
      method: 'POST',
      body: payload,
    });
    setAccessToken(data.accessToken);
    setCustomer(data.customer);
    return data;
  }, []);

  const logout = useCallback(async () => {
    try {
      await api('/auth/customer/logout', { method: 'POST' });
    } catch {
      // best effort
    }
    setAccessToken(null);
    setCustomer(null);
  }, []);

  return (
    <AuthContext.Provider value={{ customer, loading, login, signup, logout, isAuthenticated: !!customer }}>
      {children}
    </AuthContext.Provider>
  );
}

export function useAuth() {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside AuthProvider');
  return ctx;
}
